import type { FC } from 'react';
import { AlertTriangle } from 'lucide-react';
import type {
  ShipmentHandoverState,
  ShipmentOnecDiscrepancy,
  ShipmentOrigin,
  ShipmentStatus,
} from '@/api';
import { Badge, cn } from '@/ui';

import {
  HANDOVER_STATES,
  ONEC_DISCREPANCIES,
  SHIPMENT_STATUSES,
} from '../lib/shipmentStatus';

export interface ShipmentBadgesProps {
  status: ShipmentStatus;
  handoverState: ShipmentHandoverState;
  /** Расхождения с заказом 1С; есть хоть одно - бейдж «Расхождение с 1С». */
  onecDiscrepancies?: ReadonlyArray<ShipmentOnecDiscrepancy>;
  /** Отгрузка помечена задержанной - бейдж «Задержано», причина в подсказке. */
  isDelayed?: boolean;
  delayReason?: string | null;
  className?: string;
}

/**
 * Статус отгрузки и, если идёт или сорвалась, передача в 1С. Передача - отдельное
 * измерение поверх статуса (ADR-007), поэтому и бейдж отдельный. Расхождение с
 * заказом 1С (ADR-012) - тоже отдельный бейдж: статус из 1С двигается только
 * вперёд, а всё остальное человек должен увидеть. Задержка - метка менеджера
 * поверх статуса, DelayBadge макета.
 */
export const ShipmentBadges: FC<ShipmentBadgesProps> = ({
  status,
  handoverState,
  onecDiscrepancies = [],
  isDelayed = false,
  delayReason,
  className,
}) => {
  const statusMeta = SHIPMENT_STATUSES[status];
  const handoverMeta = HANDOVER_STATES[handoverState];

  return (
    <span
      className={cn('inline-flex flex-wrap items-center gap-1.5', className)}
    >
      <Badge tone={statusMeta.tone} withDot>
        {statusMeta.label}
      </Badge>
      {handoverMeta !== null && (
        <Badge tone={handoverMeta.tone}>{handoverMeta.label}</Badge>
      )}
      {isDelayed && (
        <Badge tone="delayed" title={delayReason ?? undefined}>
          <AlertTriangle aria-hidden className="size-[11px]" /> Задержано
        </Badge>
      )}
      {onecDiscrepancies.length > 0 && (
        <Badge
          tone="delayed"
          title={onecDiscrepancies
            .map((discrepancy) => ONEC_DISCREPANCIES[discrepancy])
            .join('\n')}
        >
          Расхождение с 1С
        </Badge>
      )}
    </span>
  );
};

/**
 * Происхождение отгрузки: «1С» - создана по заказу 1С, а не у нас (номер
 * заказа - в подсказке), у своей - как создана (CreationBadge). Номер у всех
 * сквозной, без префикса, поиск и сортировка по нему едины (ADR-012, п. 7), поэтому
 * происхождение - бейджем: в «Отгрузках в работе» и «Истории» рядом со статусом,
 * в «Плане» - под клиентом.
 */
export const ShipmentOriginBadge: FC<{
  origin: ShipmentOrigin;
  customerOrderNumber?: string | null;
  isForecast?: boolean;
}> = ({ origin, customerOrderNumber, isForecast = false }) =>
  origin === 'Onec' ? (
    <Badge
      title={
        customerOrderNumber
          ? `Создана по заказу 1С № ${customerOrderNumber}`
          : 'Создана по заказу 1С'
      }
    >
      1С
    </Badge>
  ) : (
    <CreationBadge isForecast={isForecast} />
  );

/** Как создана своя отгрузка: «Авто» - планом из истории, «Вручную» - менеджером. */
export const CreationBadge: FC<{ isForecast: boolean }> = ({ isForecast }) =>
  isForecast ? (
    <Badge
      tone="plan"
      title="Создана планом из истории по ритму прошлых поставок"
    >
      Авто
    </Badge>
  ) : (
    <Badge title="Создана менеджером вручную">Вручную</Badge>
  );
